'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Link from 'next/link';
import type { ColDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import dynamic from 'next/dynamic';
import { useFavourites } from '@/app/_hooks/use-favourites';
import { useLocalStorage } from '@/app/_hooks/use-location-storage';
import { type Course } from '@/types/CoursesV2Response';
import { type FormattedCourse, formatAgeDisplay, formatCourseData } from '../_lib/format-course-data';
import { formatOccurrenceDescription } from '../_lib/format-occurrence-description';
import { Checkbox } from './Checkbox';
import { FavouriteCell } from './FavouriteCell';
import { HeartIcon } from './HeartIcon';
const DesktopGrid = dynamic(() => import('./DesktopGrid').then(module => module.DesktopGrid), {
  ssr: false,
  loading: () => <div className="flex items-center justify-center py-8">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
      </div>
});
type GridProps = {
  org: string;
  courses: Course[];
};
export const Grid = t0 => {
  const $ = _c(109);
  const {
    org,
    courses
  } = t0;
  const gridRef = useRef(null);
  const {
    isFavourite,
    toggleFavourite
  } = useFavourites();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      upcoming: false,
      spotsAvailable: false,
      weekend: false,
      age: {
        years: undefined as number | undefined,
        months: undefined as number | undefined
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [filters, setFilters] = useLocalStorage("courseFilters", t1);
  const [isDesktop, setIsDesktop] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const mediaQuery = window.matchMedia("(min-width: 1025px)");
      const handleChange = () => setIsDesktop(mediaQuery.matches);
      handleChange();
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const mediaQuery_0 = window.matchMedia("(prefers-color-scheme: dark)");
      const handleChange_0 = () => setIsDarkMode(mediaQuery_0.matches);
      handleChange_0();
      mediaQuery_0.addEventListener("change", handleChange_0);
      return () => mediaQuery_0.removeEventListener("change", handleChange_0);
    };
    t5 = [];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let map;
  if ($[5] !== courses) {
    map = new Map();
    for (const course of courses) {
      map.set(course.EventId, course);
    }
    $[5] = courses;
    $[6] = map;
  } else {
    map = $[6];
  }
  const coursesById = map;
  let t6;
  if ($[7] !== courses) {
    t6 = courses.map(formatCourseData);
    $[7] = courses;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const rowData = t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      headerName: "Name",
      field: "EventName",
      sort: "asc",
      pinned: "left",
      tooltipField: "Details",
      floatingFilter: true
    };
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== coursesById || $[11] !== org) {
    t8 = {
      headerName: "",
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      resizable: false,
      sortable: false,
      filter: false,
      pinned: "left",
      suppressAutoSize: true,
      suppressSizeToFit: true,
      cellRenderer: t9 => {
        const {
          data
        } = t9;
        const originalCourse = coursesById.get(data.EventId);
        if (!originalCourse) {
          return null;
        }
        return <FavouriteCell course={originalCourse} org={org} />;
      }
    };
    $[10] = coursesById;
    $[11] = org;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      headerName: "No.",
      field: "CourseIdTrimmed",
      width: 100
    };
    t10 = {
      headerName: "Start",
      field: "OccurrenceMinStartDate",
      sortable: true,
      filter: "agDateColumnFilter",
      width: 120
    };
    t11 = {
      headerName: "End",
      field: "OccurrenceMaxStartDate",
      sortable: true,
      filter: "agDateColumnFilter",
      width: 120
    };
    t12 = {
      headerName: "Occurs",
      field: "OccurrenceDescription",
      width: 150
    };
    t13 = {
      headerName: "Time",
      field: "EventTimeDescription",
      width: 200
    };
    t14 = {
      headerName: "Location",
      field: "FacilityLocation",
      floatingFilter: true,
      width: 300
    };
    t15 = {
      headerName: "Min age",
      field: "MinimumAge",
      valueFormatter: _temp,
      width: 120
    };
    t16 = {
      headerName: "Max age",
      field: "MaximumAge",
      valueFormatter: _temp2,
      width: 120
    };
    t17 = {
      headerName: "Price",
      field: "PriceRange",
      width: 120
    };
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
    $[18] = t15;
    $[19] = t16;
    $[20] = t17;
    $[21] = t9;
  } else {
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
    t14 = $[17];
    t15 = $[18];
    t16 = $[19];
    t17 = $[20];
    t9 = $[21];
  }
  let t18;
  if ($[22] !== org) {
    t18 = {
      headerName: "Spots",
      field: "spots",
      pinned: "right",
      width: 120,
      cellRenderer: t19 => {
        const {
          data: data_0,
          value: value_1
        } = t19;
        const href = `https://${org}.perfectmind.com/Clients/BookMe4LandingPages/CoursesLandingPage?courseId=${data_0.EventId}`;
        return <Link href={href} target="_blank" rel="noopener noreferrer">{value_1}</Link>;
      }
    };
    $[22] = org;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== t18 || $[25] !== t8) {
    t19 = [t7, t8, t9, t10, t11, t12, t13, t14, t15, t16, t17, t18];
    $[24] = t18;
    $[25] = t8;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const columnDefs = t19;
  let t20;
  if ($[27] !== filters.age.months || $[28] !== filters.age.years || $[29] !== filters.spotsAvailable || $[30] !== filters.upcoming || $[31] !== filters.weekend) {
    t20 = data_1 => {
      let todayBoundary = null;
      if (filters.upcoming) {
        const today = new Date();
        todayBoundary = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      }
      const isWeekendRegex = /Sat|Sun/;
      const spotsAvailableRegex = /Closed|Wait|Full/i;
      return data_1.filter(row => {
        if (filters.upcoming && todayBoundary) {
          if (row.OccurrenceMinStartDate < todayBoundary) {
            return false;
          }
        }
        if (filters.weekend) {
          if (!isWeekendRegex.test(row.OccurrenceDescription)) {
            return false;
          }
        }
        if (filters.spotsAvailable) {
          if (spotsAvailableRegex.test(row.spots)) {
            return false;
          }
        }
        if (filters.age.years !== undefined || filters.age.months !== undefined) {
          const ageValue = (filters.age.years ?? 0) + (filters.age.months ?? 0) / 12;
          if (!(row.MinimumAge <= ageValue && row.MaximumAge >= ageValue)) {
            return false;
          }
        }
        return true;
      });
    };
    $[27] = filters.age.months;
    $[28] = filters.age.years;
    $[29] = filters.spotsAvailable;
    $[30] = filters.upcoming;
    $[31] = filters.weekend;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const filterRowData = t20;
  let t21;
  if ($[33] !== filters.age.months || $[34] !== filters.age.years || $[35] !== filters.spotsAvailable || $[36] !== filters.upcoming || $[37] !== filters.weekend) {
    t21 = async () => {
      if (!gridRef.current?.api) {
        return;
      }
      const filterModel = {};
      if (filters.upcoming) {
        filterModel.OccurrenceMinStartDate = {
          type: "greaterThan",
          dateFrom: new Date().toISOString().split("T")[0]
        };
      }
      if (filters.weekend) {
        filterModel.OccurrenceDescription = {
          filterType: "text",
          operator: "OR",
          conditions: [{
            filterType: "text",
            type: "contains",
            filter: "Sat"
          }, {
            filterType: "text",
            type: "contains",
            filter: "Sun"
          }]
        };
      }
      if (filters.spotsAvailable) {
        filterModel.spots = {
          filterType: "text",
          operator: "AND",
          conditions: [{
            filterType: "text",
            type: "notContains",
            filter: "Closed"
          }, {
            filterType: "text",
            type: "notContains",
            filter: "Wait"
          }, {
            filterType: "text",
            type: "notContains",
            filter: "Full"
          }]
        };
      }
      if (filters.age.years !== undefined || filters.age.months !== undefined) {
        const ageValue_0 = (filters.age.years ?? 0) + (filters.age.months ?? 0) / 12;
        filterModel.MinimumAge = {
          filterType: "number",
          type: "lessThanOrEqual",
          filter: ageValue_0
        };
        filterModel.MaximumAge = {
          filterType: "number",
          type: "greaterThanOrEqual",
          filter: ageValue_0
        };
      }
      gridRef.current?.api.setFilterModel(filterModel);
    };
    $[33] = filters.age.months;
    $[34] = filters.age.years;
    $[35] = filters.spotsAvailable;
    $[36] = filters.upcoming;
    $[37] = filters.weekend;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const applyFilters = t21;
  let t22;
  if ($[39] !== applyFilters) {
    t22 = () => {
      applyFilters();
    };
    $[39] = applyFilters;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== applyFilters || $[42] !== filters) {
    t23 = [filters, applyFilters];
    $[41] = applyFilters;
    $[42] = filters;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  useEffect(t22, t23);
  let t24;
  if ($[44] !== setFilters) {
    t24 = () => {
      setFilters({
        upcoming: false,
        spotsAvailable: false,
        weekend: false,
        age: {
          years: undefined,
          months: undefined
        }
      });
    };
    $[44] = setFilters;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  const resetFilters = t24;
  let t25;
  if ($[46] !== filters || $[47] !== setFilters) {
    t25 = checked => setFilters({
      ...filters,
      upcoming: checked
    });
    $[46] = filters;
    $[47] = setFilters;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== filters.upcoming || $[50] !== t25) {
    t26 = <Checkbox checked={filters.upcoming} onChange={t25} label="Upcoming events" />;
    $[49] = filters.upcoming;
    $[50] = t25;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  let t27;
  if ($[52] !== filters || $[53] !== setFilters) {
    t27 = checked_0 => setFilters({
      ...filters,
      spotsAvailable: checked_0
    });
    $[52] = filters;
    $[53] = setFilters;
    $[54] = t27;
  } else {
    t27 = $[54];
  }
  let t28;
  if ($[55] !== filters.spotsAvailable || $[56] !== t27) {
    t28 = <Checkbox checked={filters.spotsAvailable} onChange={t27} label="Available spots" />;
    $[55] = filters.spotsAvailable;
    $[56] = t27;
    $[57] = t28;
  } else {
    t28 = $[57];
  }
  let t29;
  if ($[58] !== filters || $[59] !== setFilters) {
    t29 = checked_1 => setFilters({
      ...filters,
      weekend: checked_1
    });
    $[58] = filters;
    $[59] = setFilters;
    $[60] = t29;
  } else {
    t29 = $[60];
  }
  let t30;
  if ($[61] !== filters.weekend || $[62] !== t29) {
    t30 = <Checkbox checked={filters.weekend} onChange={t29} label="Weekend" />;
    $[61] = filters.weekend;
    $[62] = t29;
    $[63] = t30;
  } else {
    t30 = $[63];
  }
  let t31;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <span>Age</span>;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  const t32 = filters.age.years ?? "";
  let t33;
  if ($[65] !== filters || $[66] !== setFilters) {
    t33 = e => setFilters({
      ...filters,
      age: {
        ...filters.age,
        years: e.target.value ? parseInt(e.target.value) : undefined
      }
    });
    $[65] = filters;
    $[66] = setFilters;
    $[67] = t33;
  } else {
    t33 = $[67];
  }
  let t34;
  if ($[68] !== t32 || $[69] !== t33) {
    t34 = <input aria-label="Years" className="w-16 bg-white px-2 py-1 dark:bg-gray-700" min="0" placeholder="yy" step="1" type="number" value={t32} onChange={t33} />;
    $[68] = t32;
    $[69] = t33;
    $[70] = t34;
  } else {
    t34 = $[70];
  }
  const t35 = filters.age.months ?? "";
  let t36;
  if ($[71] !== filters || $[72] !== setFilters) {
    t36 = e_0 => setFilters({
      ...filters,
      age: {
        ...filters.age,
        months: e_0.target.value ? parseInt(e_0.target.value) : undefined
      }
    });
    $[71] = filters;
    $[72] = setFilters;
    $[73] = t36;
  } else {
    t36 = $[73];
  }
  let t37;
  if ($[74] !== t35 || $[75] !== t36) {
    t37 = <input aria-label="Months" className="w-16 bg-white px-2 py-1 dark:bg-gray-700" max="11" min="0" placeholder="mm" step="1" type="number" value={t35} onChange={t36} />;
    $[74] = t35;
    $[75] = t36;
    $[76] = t37;
  } else {
    t37 = $[76];
  }
  let t38;
  if ($[77] !== t34 || $[78] !== t37) {
    t38 = <label className="flex items-center gap-2">{t31}{t34}{t37}</label>;
    $[77] = t34;
    $[78] = t37;
    $[79] = t38;
  } else {
    t38 = $[79];
  }
  let t39;
  if ($[80] !== resetFilters) {
    t39 = <button className="anchor cursor-pointer" onClick={resetFilters} type="button">Reset all filters</button>;
    $[80] = resetFilters;
    $[81] = t39;
  } else {
    t39 = $[81];
  }
  let t40;
  if ($[82] !== t26 || $[83] !== t28 || $[84] !== t30 || $[85] !== t38 || $[86] !== t39) {
    t40 = <form className="flex flex-wrap gap-6 rounded-lg border bg-white px-4 py-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">{t26}{t28}{t30}{t38}{t39}</form>;
    $[82] = t26;
    $[83] = t28;
    $[84] = t30;
    $[85] = t38;
    $[86] = t39;
    $[87] = t40;
  } else {
    t40 = $[87];
  }
  let t41;
  if ($[88] !== isDesktop) {
    t41 = isDesktop === null && <div className="flex items-center justify-center py-8"><div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" /></div>;
    $[88] = isDesktop;
    $[89] = t41;
  } else {
    t41 = $[89];
  }
  let t42;
  if ($[90] !== applyFilters || $[91] !== columnDefs || $[92] !== isDarkMode || $[93] !== isDesktop || $[94] !== rowData) {
    t42 = isDesktop === true && <DesktopGrid columnDefs={columnDefs} rowData={rowData} isDarkMode={isDarkMode} applyFilters={applyFilters} ref={gridRef} />;
    $[90] = applyFilters;
    $[91] = columnDefs;
    $[92] = isDarkMode;
    $[93] = isDesktop;
    $[94] = rowData;
    $[95] = t42;
  } else {
    t42 = $[95];
  }
  let t43;
  if ($[96] !== coursesById || $[97] !== filterRowData || $[98] !== isDesktop || $[99] !== isFavourite || $[100] !== org || $[101] !== rowData || $[102] !== toggleFavourite) {
    t43 = isDesktop === false && <div className="flex flex-col gap-2">{filterRowData(rowData).sort(_temp3).map(row_0 => {
        const href_0 = `https://${org}.perfectmind.com/Clients/BookMe4LandingPages/CoursesLandingPage?courseId=${row_0.EventId}`;
        return <details key={row_0.EventId} className="rounded-lg border bg-white px-4 py-3 shadow dark:border-gray-700 dark:bg-gray-800"><summary className="flex cursor-pointer items-center justify-between gap-4"><div className="flex items-center gap-2"><HeartIcon filled={isFavourite(row_0.EventId)} onClick={() => {
                const originalCourse_0 = coursesById.get(row_0.EventId);
                if (originalCourse_0) {
                  toggleFavourite(originalCourse_0, org);
                }
              }} />{formatOccurrenceDescription(row_0.EventName, row_0.OccurrenceMinStartDate, row_0.OccurrenceDescription)}</div><Link href={href_0} target="_blank" rel="noopener noreferrer" className="whitespace-nowrap text-blue-600 underline" onClick={_temp4}>{row_0.spots}</Link></summary><table className="mt-2 w-full text-sm"><tbody><tr hidden={true}><th className="pt-2 pr-4 text-start align-top font-semibold">Number:</th><td className="pt-2">{row_0.CourseIdTrimmed}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Description:</th><td className="pt-2">{row_0.Details}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Start:</th><td className="pt-2">{row_0.OccurrenceMinStartDate.toLocaleDateString()}</td></tr>{row_0.OccurrenceMaxStartDate && <tr><th className="pt-2 pr-4 text-start align-top font-semibold">End:</th><td className="pt-2">{row_0.OccurrenceMaxStartDate.toLocaleDateString()}</td></tr>}<tr><th className="pt-2 pr-4 text-start align-top font-semibold">Occurs:</th><td className="pt-2">{row_0.OccurrenceDescription}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Time:</th><td className="pt-2">{row_0.EventTimeDescription}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Location:</th><td className="pt-2">{row_0.FacilityLocation}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Min age:</th><td className="pt-2">{formatAgeDisplay(row_0.MinimumAge)}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Max age:</th><td className="pt-2">{formatAgeDisplay(row_0.MaximumAge)}</td></tr><tr><th className="pt-2 pr-4 text-start align-top font-semibold">Price:</th><td className="pt-2">{row_0.PriceRange}</td></tr></tbody></table></details>;
      })}</div>;
    $[96] = coursesById;
    $[97] = filterRowData;
    $[98] = isDesktop;
    $[99] = isFavourite;
    $[100] = org;
    $[101] = rowData;
    $[102] = toggleFavourite;
    $[103] = t43;
  } else {
    t43 = $[103];
  }
  let t44;
  if ($[104] !== t40 || $[105] !== t41 || $[106] !== t42 || $[107] !== t43) {
    t44 = <div className="flex flex-col gap-4">{t40}{t41}{t42}{t43}</div>;
    $[104] = t40;
    $[105] = t41;
    $[106] = t42;
    $[107] = t43;
    $[108] = t44;
  } else {
    t44 = $[108];
  }
  return t44;
};
function _temp(t0) {
  const {
    value
  } = t0;
  return formatAgeDisplay(value);
}
function _temp2(t0) {
  const {
    value: value_0
  } = t0;
  return formatAgeDisplay(value_0);
}
function _temp3(a, b) {
  return a.EventName.localeCompare(b.EventName);
}
function _temp4(e_1) {
  return e_1.stopPropagation();
}